import dynamic from 'next/dynamic';
import React from 'react';
import { Modules } from '~/lib/data-contract';
import { ErrorBoundary } from '~/shared/utils/errorBoundary';
import { ErrorFallbackContent } from '../ErrorFallbackContent/ErrorFallbackContent';
import { M140Anchor } from '../M140AnchorModule/M140AnchorModule';
import M12Hero5050Module from '../M12Hero5050Module/M12Hero5050Module';
import M13Countdown5050Module from '../M13Countdown5050Module/M13Countdown5050Module';
import M40ContentCardRow from '../M40ContentCardRow/M40ContentCardRow';

const M10Hero = dynamic(() => import('../M10Hero'));
const M11Hero = dynamic(() => import('../M11Hero'));
const M22Brands = dynamic(() => import('../M22Brands'));
const M20Categories = dynamic(() => import('../M20Categories'));
const M21ReferencedCategories = dynamic(() => import('../M21ReferencedCategories'));
const M60USP = dynamic(() => import('../M60USP'));
const M70Media = dynamic(() => import('../M70Media'));
const M75MediaText = dynamic(() => import('../M75MediaText'));
const M80MediaTextBundle = dynamic(() => import('../M80MediaTextBundle'));
const M90Quote = dynamic(() => import('../M90Quote'));
const M100RichText = dynamic(() => import('../M100RichText'));
const M120Accordion = dynamic(() => import('../M120Accordion'));
const M30ProductCarousel = dynamic(() => import('../M30ProductCarousel'));
const M31RaptorProductCarousel = dynamic(() => import('../M31RaptorProductCarousel'));
const M115ShopTheLookModule = dynamic(() => import('../M115ShopTheLookModule'));

type DynamicBlockProps = {
    element: Modules;
    index?: number;
    priority: boolean;
    productId?: string;
    productBrand?: string;
    productCategory?: string;
    isLastItem?: boolean;
};

export const DynamicBlock = ({ element, index, ...rest }: DynamicBlockProps) => {
    return (
        <ErrorBoundary fallback={<ErrorFallbackContent />}>
            {getType({ element, index, ...rest })}
        </ErrorBoundary>
    );
};

export const getType = ({ isLastItem, element, index, ...rest }: DynamicBlockProps) => {
    switch (element.type) {
        case 'm10HeroModule':
            return (
                <M10Hero
                    {...element}
                    spacingTop={index === 0 && element.spacingTop ? element.spacingTop : 'none'}
                    aboveTheFold={index === 0 && element.aboveTheFold !== false}
                    {...rest}
                />
            );
        case 'm11HeroModule':
            return (
                <M11Hero
                    {...element}
                    aboveTheFold={index === 0 && element.aboveTheFold !== false}
                    {...rest}
                />
            );

        case 'm22BrandsModule':
            return <M22Brands {...element} isLastItem={isLastItem} {...rest} />;

        case 'm20CategoriesModule':
            return <M20Categories {...element} isLastItem={isLastItem} {...rest} />;

        case 'm21ReferencedCategoriesModule':
            return <M21ReferencedCategories {...element} isLastItem={isLastItem} {...rest} />;
        case 'm30ProductCarouselModule':
            return <M30ProductCarousel {...element} isLastItem={isLastItem} {...rest} />;
        case 'm31RaptorCarouselModule':
            return <M31RaptorProductCarousel {...element} isLastItem={isLastItem} {...rest} />;
        case 'm60UspModule':
            return <M60USP {...element} {...rest} />;

        case 'm70MediaModule':
            return <M70Media {...element} {...rest} />;

        case 'm75MediaAndTextModule':
            return <M75MediaText {...element} {...rest} />;

        case 'm80MediaAndTextBundleModule':
            return <M80MediaTextBundle {...element} {...rest} />;

        case 'm90QuoteModule':
            return <M90Quote {...element} {...rest} />;

        case 'm100RichTextModule':
            return <M100RichText {...element} {...rest} />;

        case 'm115ShopTheLookModule':
            return (
                <M115ShopTheLookModule
                    {...element}
                    aboveTheFold={index === 0 && element.aboveTheFold !== false}
                    {...rest}
                />
            );

        case 'm120AccordionListModule':
            return <M120Accordion {...element} {...rest} />;

        case 'm140AnchorModule':
            return <M140Anchor {...element} {...rest} />;
        case 'm12Hero5050Module':
            return (
                <M12Hero5050Module
                    {...element}
                    aboveTheFold={index === 0 && element.aboveTheFold !== false}
                    {...rest}
                />
            );
        case 'm13Countdown5050Module':
            return (
                <M13Countdown5050Module
                    {...element}
                    aboveTheFold={index === 0 && element.aboveTheFold !== false}
                    {...rest}
                />
            );
        case 'm40ContentCardRow':
            return <M40ContentCardRow {...element} {...rest} />;

        default:
            return null;
    }
};
